import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router";
import { motion } from "motion/react";
import {
  ArrowRight, ArrowLeft, Phone, ChevronRight, CheckCircle2, Loader2, Play
} from "lucide-react";
import { servicesService, parseFeatures, parseBenefits, parseFaqs, type Service } from "../../lib/services/servicesService";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import * as Icons from "lucide-react";

import { SEO } from "./SEO";

export function ServiceDetailPage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();

  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [allServices, setAllServices] = useState<Service[]>([]);

  useEffect(() => {
    servicesService.list().then(data => {
      const active = data.filter(s => s.status === "Active").sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
      setAllServices(active);
      const found = active.find(s => s.slug === serviceId);
      setService(found || null);
    }).catch(console.error).finally(() => setLoading(false));
  }, [serviceId]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center py-20 text-center px-6">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2d50]/40 mb-4" />
        <p className="text-[#1a2d50] font-medium">Loading Service Details...</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center py-20 text-center px-6">
        <h1 className="text-3xl text-[#1a2d50] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
          Service Not Found
        </h1>
        <p className="text-[#1a2d50]/50 mb-8">This service doesn't exist or may have been moved.</p>
        <Link to="/services" className="inline-flex items-center gap-2 bg-[#E9A319] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#d4920f] transition-all">
          <ArrowLeft className="w-4 h-4" /> Back to Services
        </Link>
      </div>
    );
  }

  const currentIndex = allServices.findIndex((s) => s.slug === serviceId);
  const prevService = currentIndex > 0 ? allServices[currentIndex - 1] : null;
  const nextService = currentIndex < allServices.length - 1 ? allServices[currentIndex + 1] : null;

  const Icon = (Icons as any)[service.icon] || Icons.Settings;
  const feats = parseFeatures(service.features);
  const bens = parseBenefits(service.benefits);
  const faqs = parseFaqs(service.faqs);

  const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
  const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

  return (
    <div>
      <SEO
        title={`${service.shortTitle} | Solace SMART Solutions`}
        description={service.desc}
        ogImage={service.img}
      />
      {/* ── HERO ── */}
      <section className="relative bg-[#0e1c35] overflow-hidden">
        <div className="absolute inset-0">
          <ImageWithFallback src={service.img} alt={service.title} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0e1c35] via-[#0e1c35]/85 to-[#0e1c35]/50" />
        </div>

        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16 relative z-10">
          <motion.nav
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="flex items-center gap-1.5 text-xs text-white/35 mb-8"
          >
            <Link to="/" className="hover:text-white/60 transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/services" className="hover:text-white/60 transition-colors">Services</Link>
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: service.colorAccent || '#E9A319' }}>{service.shortTitle}</span>
          </motion.nav>

          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2.5 mb-5"
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${service.colorAccent || '#E9A319'}20`, border: `1px solid ${service.colorAccent || '#E9A319'}35` }}>
                <Icon className="w-4 h-4" style={{ color: service.colorAccent || '#E9A319' }} />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: service.colorAccent || '#E9A319' }}>
                Our Service
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 }}
              className="text-3xl md:text-5xl text-white leading-tight mb-4"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}
            >
              {service.title}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.16 }}
              className="text-white/55 leading-relaxed mb-8 text-base"
            >
              {service.desc}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.24 }}
              className="flex flex-wrap gap-3"
            >
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-sm font-bold bg-[#E9A319] hover:bg-[#d4920f] transition-all active:scale-95 shadow-lg"
              >
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+918800878095"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white text-sm font-semibold hover:bg-white/8 transition-all"
              >
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── OVERVIEW: Image + Benefits ── */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid lg:grid-cols-2 gap-10 items-start"
          >
            {/* Image */}
            <motion.div variants={fadeUp}>
              <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]">
                <ImageWithFallback src={service.img} alt={service.title} className="w-full h-full object-cover" />
                <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm bg-white/90 shadow-lg">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${service.colorAccent || '#E9A319'}25` }}>
                    <Icon className="w-4 h-4" style={{ color: service.colorAccent || '#E9A319' }} />
                  </div>
                  <span className="text-[#1a2d50] text-sm font-semibold">{service.shortTitle}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: service.colorAccent || '#E9A319' }}>Certified</span>
                </div>
              </div>
            </motion.div>

            {/* Benefits */}
            <motion.div variants={stagger}>
              <motion.div variants={fadeUp} className="mb-5">
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: service.colorAccent || '#E9A319' }}>
                  Why Choose This
                </span>
                <h2 className="text-2xl md:text-3xl text-[#1a2d50] mt-1.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                  Key Benefits
                </h2>
              </motion.div>

              <div className="grid grid-cols-2 gap-2.5">
                {bens.map((benefit, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    className="p-4 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all bg-white"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-7 h-7 shrink-0 rounded-lg bg-green-50 text-green-600 flex items-center justify-center border border-green-100">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="font-bold text-[#1a2d50] text-sm mb-1">{benefit.title}</h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">{benefit.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── DETAILS: Features ── */}
      <section className="py-16 bg-[#f8f6f1]">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="flex flex-col lg:flex-row gap-12 items-center"
          >
            <motion.div variants={fadeUp} className="lg:w-1/3">
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: service.colorAccent || '#E9A319' }}>
                Capabilities
              </span>
              <h2 className="text-2xl md:text-3xl text-[#1a2d50] mt-1.5 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                What's Included
              </h2>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                Our comprehensive package covers everything needed to deliver an optimum cost solution with Japanese engineering standards.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border border-[#1a2d50]/15 text-[#1a2d50] px-5 py-2.5 rounded-full font-semibold hover:bg-[#1a2d50] hover:text-white transition-all text-sm"
              >
                Request Proposal <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div variants={stagger} className="lg:w-2/3 grid sm:grid-cols-2 gap-3">
              {feats.map((feature, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-transparent hover:border-[#E9A319]/20 transition-colors"
                >
                  <div className="w-6 h-6 shrink-0 rounded-full flex items-center justify-center mt-0.5" style={{ backgroundColor: `${service.colorAccent || '#E9A319'}1A`, color: service.colorAccent || '#E9A319' }}>
                    <Play className="w-3 h-3 fill-current" />
                  </div>
                  <span className="text-sm font-medium text-[#1a2d50] leading-snug">{feature}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQS ── */}
      {faqs.length > 0 && (
        <section className="py-16 bg-white border-b border-gray-100">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: service.colorAccent || '#E9A319' }}>
                Clarifications
              </span>
              <h2 className="text-2xl md:text-3xl text-[#1a2d50] mt-1.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-gray-50/50 border border-gray-100 p-5 rounded-2xl">
                  <h4 className="font-bold text-[#1a2d50] text-sm md:text-base mb-2 flex items-start gap-2">
                    <span style={{ color: service.colorAccent || '#E9A319' }}>Q.</span> {faq.q}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed pl-6">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── PAGINATION (Prev/Next Service) ── */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          {prevService ? (
            <Link
              to={`/services/${prevService.slug}`}
              className="flex items-center gap-3 group px-4 py-2 hover:bg-white rounded-xl transition-colors"
            >
              <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 group-hover:border-[#E9A319] group-hover:text-[#E9A319] transition-colors">
                <ArrowLeft className="w-4 h-4" />
              </div>
              <div className="hidden sm:block">
                <span className="block text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-0.5">Previous</span>
                <span className="block text-sm font-bold text-[#1a2d50]">{prevService.shortTitle}</span>
              </div>
            </Link>
          ) : <div />}

          {nextService ? (
            <Link
              to={`/services/${nextService.slug}`}
              className="flex items-center gap-3 group px-4 py-2 hover:bg-white rounded-xl transition-colors text-right"
            >
              <div className="hidden sm:block">
                <span className="block text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-0.5">Next Service</span>
                <span className="block text-sm font-bold text-[#1a2d50]">{nextService.shortTitle}</span>
              </div>
              <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 group-hover:border-[#E9A319] group-hover:text-[#E9A319] transition-colors">
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ) : <div />}
        </div>
      </section>
    </div>
  );
}
